import {
	BlockType,
	ContentBlock,
	SSEEvent,
	SelectedVersions,
	UIDocumentRef,
	UIMessage,
	UIMessageVersions,
} from "./Chat.types";
import { UIComponent } from "../../types/uiComponent";

/**
 * Creates a user message object
 */
export const createUserMessage = (text: string, documentRefs?: UIDocumentRef[]): UIMessage => {
		return {
			id: `user-${Date.now()}`,
			role: "user" as const,
			content_blocks: [
				{ type: BlockType.TEXT, text, ui_component_type: UIComponent.TEXT },
			],
			documentRefs: documentRefs && documentRefs.length > 0 ? documentRefs : undefined,
		};
	};

/**
 * Creates an empty assistant message object (populated during streaming)
 */
export const createAssistantMessage = (id?: string): UIMessage => {
	return {
		id: id ?? `assistant-${Date.now()}`,
		role: "assistant" as const,
		content_blocks: [],
	};
};

/** The text a user message was sent with — its text blocks, joined. */
export const userMessageText = (message: UIMessage): string =>
	message.content_blocks
		.map((block) => (block.type === BlockType.TEXT ? block.text : ""))
		.filter((text) => text.length > 0)
		.join("\n")
		.trim();

/** The persisted id a `history-<n>` message carries, or undefined. */
export const persistedMessageId = (uiMessageId: string): number | undefined => {
	const match = /^history-(\d+)$/.exec(uiMessageId);
	return match ? Number(match[1]) : undefined;
};

/**
 * The messages the reader should see, given which answer versions they chose.
 *
 * The server marks every message with its place in the live thread (the newest
 * version of each question, and what followed it). Selecting an OLDER version
 * of a question shows that version in the answer's slot, the turns that
 * followed it before it was replaced (its branch), and nothing of the live
 * continuation after the newest version — the conversation as it stood then.
 * Older versions are read-only; the newest is always where the chat continues.
 */
export const visibleMessages = (
	messages: readonly UIMessage[],
	selected: SelectedVersions = {},
): UIMessage[] => {
	const position = new Map<string, number>();
	messages.forEach((message, index) => position.set(message.id, index));

	// Per question: the newest version, and the version the reader picked. A
	// pick only counts when that version is actually loaded — history is
	// paginated, and an older version may sit on a page not fetched yet; the
	// page loads it before selecting, but the slot must never render empty.
	const loaded = new Set<number>();
	for (const message of messages) {
		const persisted = persistedMessageId(message.id);
		if (persisted !== undefined) loaded.add(persisted);
	}
	const newestOf = new Map<number, number>();
	const chosenOf = new Map<number, number>();
	for (const message of messages) {
		const versions = message.versions;
		if (!versions || versions.total < 2) continue;
		const newest = versions.ids[versions.ids.length - 1];
		newestOf.set(versions.questionMessageId, newest);
		const picked = selected[versions.questionMessageId];
		chosenOf.set(
			versions.questionMessageId,
			picked !== undefined && versions.ids.includes(picked) && loaded.has(picked)
				? picked
				: newest,
		);
	}
	const forkedAt: number[] = [];
	for (const [question, newest] of newestOf) {
		if (chosenOf.get(question) !== newest) {
			const at = position.get(`history-${newest}`);
			if (at !== undefined) forkedAt.push(at);
		}
	}
	const chosenVersionIds = new Set<number>();
	for (const [question, chosen] of chosenOf) {
		if (chosen !== newestOf.get(question)) chosenVersionIds.add(chosen);
	}

	return messages.filter((message, index) => {
		// A message the server has not named yet is the one being generated
		// right now; it is on the live thread by construction.
		if (persistedMessageId(message.id) === undefined) {
			return message.thread?.live !== false;
		}
		const versions = message.versions;
		if (versions && versions.total > 1) {
			return (
				persistedMessageId(message.id) ===
				chosenOf.get(versions.questionMessageId)
			);
		}
		if (message.thread?.live === false) {
			return (
				message.thread.branchOf !== undefined &&
				chosenVersionIds.has(message.thread.branchOf)
			);
		}
		// Live: hidden only past the fork of a question shown at an older version.
		return forkedAt.every((at) => index < at);
	});
};

/**
 * The thread as it will look once ``placeholder`` replaces ``targetId``, before
 * the server has said so: the placeholder takes the answer's slot, the old
 * answer and everything after it become its branch. History reload replaces
 * this with the server's own view when the generation ends.
 */
/** Stands in for the not-yet-persisted version in a local ``versions.ids``. */
export const PENDING_VERSION_ID = -1;

export const withLocalRegeneration = (
	messages: readonly UIMessage[],
	targetId: string,
	placeholder: UIMessage,
): UIMessage[] => {
	const index = messages.findIndex((message) => message.id === targetId);
	if (index < 0) return [...messages, placeholder];
	const target = messages[index];
	const targetPersistedId = persistedMessageId(targetId);
	const versions = target.versions;
	const grown = versions
		? {
				...versions,
				total: versions.total + 1,
				ids: [...versions.ids, PENDING_VERSION_ID],
			}
		: undefined;
	const promoted: UIMessage = {
		...placeholder,
		thread: { live: true },
		versions: grown ? { ...grown, index: grown.total } : undefined,
	};
	const branched = (message: UIMessage): UIMessage => ({
		...message,
		thread: { live: false, branchOf: targetPersistedId },
		versions:
			message.id === targetId && grown
				? { ...grown, index: versions!.index }
				: message.versions,
	});
	return [
		...messages.slice(0, index),
		...messages.slice(index).map(branched),
		promoted,
	];
};

/**
 * Once the server has named the answer a regenerate produced, the pending
 * placeholder in every ``versions.ids`` of that question becomes the real id —
 * otherwise the newest version would be an id no message carries and the slot
 * would render empty until the history reload.
 */
export const withPendingVersionResolved = (
	messages: readonly UIMessage[],
	assistantMessageId: number,
): UIMessage[] =>
	messages.map((message) =>
		message.versions?.ids.includes(PENDING_VERSION_ID)
			? {
					...message,
					versions: {
						...message.versions,
						ids: message.versions.ids.map((id) =>
							id === PENDING_VERSION_ID ? assistantMessageId : id,
						),
					},
				}
			: message,
	);

/** The persisted id of the version the reader would reach by stepping ``step``. */
export const steppedVersionId = (
	versions: UIMessageVersions,
	current: number | undefined,
	step: 1 | -1,
): number | undefined => {
	const at =
		current !== undefined
			? versions.ids.indexOf(current)
			: versions.ids.length - 1;
	const next = (at < 0 ? versions.ids.length - 1 : at) + step;
	if (next < 0 || next >= versions.ids.length) return undefined;
	return versions.ids[next];
};

/**
 * Gets standard headers for API requests
 */
export const getApiHeaders = (): Record<string, string> => {
	return {
		"Content-Type": "application/json",
	};
};

/**
 * Result type for parsing SSE lines
 */
export type ParseSSELineResult = {
	event: string | null;
	/** Incremental text token from event: message */
	textDelta?: string;
	/** A fully-parsed ContentBlock from event: block */
	newBlock?: ContentBlock;
	/** Chat title update from a block event */
	titleUpdate?: string;
	/** Error to throw / surface to the user */
	shouldThrow?: Error;
};

/**
 * Callback type for updating assistant message text delta
 */
export type UpdateMessageCallback = (messageId: string, newText: string) => void;

/**
 * Callback type for title updates received via SSE block event
 */
export type UpdateTitleCallback = (title: string) => void;

/**
 * Parses a single SSE (Server-Sent Events) line into a structured result.
 *
 * Events:
 *   event: message → text token streamed to the user (textDelta)
 *   event: block   → typed ContentBlock (TraceBlock or TextBlock) or title update
 *   event: error   → surfaces an error
 *   event: done    → stream complete, no-op
 */
export const parseSSELine = (
	line: string,
	currentEvent: string | null,
	messageId: string,
	currentText: string,
	updateMessage: UpdateMessageCallback,
	_updateMetadata?: unknown,
	updateTitle?: UpdateTitleCallback,
): ParseSSELineResult => {
	if (line.startsWith("event: ")) {
		return { event: line.slice(7).trim() };
	}

	if (line.startsWith("data: ")) {
		const data = line.slice(6);

		if (data.trim() === "[DONE]") {
			return { event: null };
		}

		if (currentEvent === SSEEvent.MESSAGE) {
			const updatedText = currentText + data;
			updateMessage(messageId, updatedText);
			return { event: null, textDelta: data };
		}

		if (currentEvent === SSEEvent.ERROR) {
			return {
				event: null,
				shouldThrow: new Error(data || "Stream generation failed"),
			};
		}

		if (currentEvent === SSEEvent.BLOCK) {
			try {
				const parsed = JSON.parse(data) as Record<string, unknown>;

				// Title update embedded as a block event
				if (parsed.type === "title" && typeof parsed.title === "string") {
					updateTitle?.(parsed.title);
					return { event: null };
				}

				// Typed content block
				if (
					parsed.type === BlockType.TEXT ||
					parsed.type === BlockType.TRACE ||
					parsed.type === BlockType.THINKING ||
					parsed.type === BlockType.CHART ||
					parsed.type === BlockType.TABLE_OVERFLOW_PENDING ||
					parsed.type === BlockType.TABLE_OVERFLOW ||
					parsed.type === BlockType.CHART_OVERFLOW_PENDING ||
					parsed.type === BlockType.CHART_OVERFLOW ||
					parsed.type === BlockType.MODIFIED_WORKBOOK ||
					parsed.type === BlockType.SCHEMA_GRAPH ||
					parsed.type === BlockType.LOAN_TAPE_PREVIEW
				) {
					return { event: null, newBlock: parsed as ContentBlock };
				}
			} catch {
				// Ignore malformed block payloads and keep stream flowing
			}
			return { event: null };
		}

		if (currentEvent === SSEEvent.DONE) {
			return { event: null };
		}
	}

	return { event: currentEvent };
};
